import { Icon } from '@components/icon/Icon';
import { IconButton } from '@components/iconButton/IconButton';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import styles from './AddonCard.scss';

export interface AddonDisplayData {
  id?: string;
  name: string;
  description: string | null;
  price: number | null;
  imageKey: string | null;
  tierNames?: string[];
}

interface AddonCardProps {
  addon: AddonDisplayData;
  onEdit?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

const formatPrice = (price: number | null): string => {
  if (price === null || price === 0) {
    return 'Free';
  }
  return `$${price.toFixed(2)}`;
};

export const AddonCard = ({
  addon,
  onEdit,
  onDelete,
  disabled = false,
}: AddonCardProps) => {
  const editTooltipId = useHtmlId('addon-card-edit');
  const deleteTooltipId = useHtmlId('addon-card-delete');
  const showActions = onEdit || onDelete;

  const imageUrl = addon.imageKey && getRestaurantImagePath(addon.imageKey);

  return (
    <div className={styles.addonCardSaved}>
      {imageUrl && (
        <img src={imageUrl} alt={addon.name} className={styles.addonImage} />
      )}
      <div className={styles.addonSavedContent}>
        <div className={styles.addonSavedInfo}>
          <div className={styles.addonTextBlock}>
            <span className={styles.addonSavedName}>{addon.name}</span>
            {addon.description?.trim() ? (
              <p className={cx(typography.t2, styles.addonSavedDescription)}>
                {addon.description}
              </p>
            ) : null}
          </div>
          <div className={styles.addonSavedPriceTiersBlock}>
            <span className={styles.addonSavedPrice}>
              {formatPrice(addon.price)}
            </span>
            {addon.tierNames && addon.tierNames.length > 0 && (
              <span className={cx(typography.t2, styles.tierBadge)}>
                <Icon name="ticket" />
                {addon.tierNames.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>
      {showActions && (
        <div className={styles.addonSavedActions}>
          <IconButton
            ariaLabel="Edit"
            className={styles.eventModalActionIconButton}
            dataTooltipId={editTooltipId}
            disabled={disabled}
            iconName="pencil"
            onClick={() => onEdit?.()}
            tooltipContent="Edit"
          />
          <IconButton
            ariaLabel="Delete"
            className={styles.eventModalActionIconButton}
            dataTooltipId={deleteTooltipId}
            disabled={disabled}
            iconName="trash"
            onClick={() => onDelete?.()}
            tooltipContent="Delete"
          />
        </div>
      )}
    </div>
  );
};
